import React, { useState } from 'react'
import { NonIdealState, Spinner } from '@blueprintjs/core'
import { useSegmentationPriorities } from '../../contexts/SegmentationPrioritiesProvider'
import { HelpDrawerSegmentationsScoresTable } from './HelpDrawer'
import { SegmentationScoresController } from './SegmentationScoresController'
import { SegmentationsScoresTable } from './SegmentationsScoresTable'
import { SegmentationScoresExportToExcelButton } from './SegmentationScoresExportToExcelButton'

/**
 * SegmentationScoresSummary orchestrates the display of segmentation scores with filtering and export functionality.
 * @param {Object} props - Component props
 * @param {Array} props.selectedSegmentations - Currently selected segmentations (external control)
 * @param {Function} props.onSelectedSegmentationsChange - Callback when selection changes (external control)
 * @returns {JSX.Element} The rendered component.
 */
export function SegmentationScoresSummary({
  selectedSegmentations: externalSelectedSegmentations,
  onSelectedSegmentationsChange: externalOnSelectionChange,
}) {
  const {
    segmentations,
    segmentationsScores,
    fetchSegmentationsScores,
    isFetchingScores,
    needsRecalculationFlag,
    lastSelectedSegmentations,
  } = useSegmentationPriorities()

  // Use external control if provided, otherwise fall back to local state
  const [localSelectedSegmentations, setLocalSelectedSegmentations] = useState(
    []
  )
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Determine which state to use
  const selectedSegmentations =
    externalSelectedSegmentations ?? localSelectedSegmentations

  // Logic to determine if selection has changed
  const hasSelectionChanged = () => {
    if (lastSelectedSegmentations.length === 0)
      return selectedSegmentations.length > 0

    const lastIds = lastSelectedSegmentations.map(seg => seg.id).sort()
    const currentIds = selectedSegmentations.map(seg => seg.id).sort()

    return (
      lastIds.length !== currentIds.length ||
      lastIds.some((id, index) => id !== currentIds[index])
    )
  }

  const handleDrawerOpen = () => setIsDrawerOpen(true)
  const handleDrawerClose = () => setIsDrawerOpen(false)

  const handleFetchScores = async options => {
    // Pass options through to provider so backend payload can be extended in a later step
    await fetchSegmentationsScores(selectedSegmentations, options)
  }

  const hasExistingScores =
    segmentationsScores && segmentationsScores.length > 0

  // Early return if we don't have segmentations loaded yet
  if (!segmentations) {
    return (
      <NonIdealState
        icon={<Spinner intent="primary" />}
        title="Loading Segmentations"
        description="Please wait while segmentations are being loaded."
      />
    )
  }

  if (isFetchingScores) {
    return (
      <NonIdealState
        icon={<Spinner intent="primary" />}
        title="Fetching Segmentation Scores"
        description="Please wait while the segmentation scores are being retrieved."
      />
    )
  }

  return (
    <div>
      <SegmentationScoresController
        selectedSegmentations={selectedSegmentations}
        onFetchScores={handleFetchScores}
        hasExistingScores={hasExistingScores}
        hasSelectionChanged={hasSelectionChanged()}
        needsRecalculationFlag={needsRecalculationFlag}
        isFetchingScores={isFetchingScores}
        onOpenHelp={handleDrawerOpen}
      />

      {hasExistingScores && (
        <>
          <SegmentationsScoresTable segmentationScores={segmentationsScores} />
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '30px',
              gap: '10px',
            }}
          >
            <SegmentationScoresExportToExcelButton
              segmentationsScores={segmentationsScores}
              disabled={isFetchingScores}
            />
          </div>
        </>
      )}

      <HelpDrawerSegmentationsScoresTable
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
      />
    </div>
  )
}
